import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { t } from "@/lib/i18n";
import { timeAgo } from "@/lib/format";
import Breadcrumbs from "@/components/Breadcrumbs";
import UserBadge from "@/components/UserBadge";
import { Skeleton } from "@/components/ui/skeleton";

export default function SearchPage() {
  const [sp] = useSearchParams();
  const q = sp.get("q") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!q) { setData({ threads: [], posts: [] }); setLoading(false); return; }
    setLoading(true);
    api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [q]);

  return (
    <div className="space-y-5" data-testid="search-page">
      <Breadcrumbs items={[{ label: t.home, to: "/" }, { label: `${t.search}: ${q}` }]} />
      <h1 className="urdu-naskh text-3xl font-semibold text-primary border-b-2 border-primary pb-2">
        {t.search}: <span className="font-eng">{q}</span>
      </h1>
      {loading && <Skeleton className="h-32 w-full" />}
      {data && (
        <div className="space-y-6">
          <section>
            <h2 className="urdu-naskh text-xl font-semibold text-primary mb-2">{t.threads} ({data.threads.length})</h2>
            <Card className="divide-y divide-border">
              {data.threads.length === 0 && <div className="p-4 urdu-naskh text-muted-foreground text-center">— {t.none}</div>}
              {data.threads.map((th) => (
                <Link to={`/t/${th.id}`} key={th.id} className="block p-4 forum-row" data-testid={`search-thread-${th.id}`}>
                  <div className="urdu-naskh text-lg text-primary">{th.title}</div>
                  <div className="flex gap-3 items-center mt-1 text-xs text-muted-foreground font-eng">
                    <UserBadge user={th.author} size="sm" showRole={false} />
                    <span>· {timeAgo(th.created_at)}</span>
                  </div>
                </Link>
              ))}
            </Card>
          </section>
          <section>
            <h2 className="urdu-naskh text-xl font-semibold text-primary mb-2">{t.posts} ({data.posts.length})</h2>
            <Card className="divide-y divide-border">
              {data.posts.length === 0 && <div className="p-4 urdu-naskh text-muted-foreground text-center">— {t.none}</div>}
              {data.posts.map((p) => (
                <Link to={`/t/${p.thread_id}#post-${p.id}`} key={p.id} className="block p-4 forum-row" data-testid={`search-post-${p.id}`}>
                  <div className="urdu-naskh text-primary">{p.thread_title}</div>
                  <div className="text-sm opacity-80 urdu-naskh mt-1 line-clamp-2">{p.snippet}</div>
                  <div className="flex gap-3 items-center mt-1 text-xs text-muted-foreground font-eng">
                    <UserBadge user={p.author} size="sm" showRole={false} />
                    <span>· {timeAgo(p.created_at)}</span>
                  </div>
                </Link>
              ))}
            </Card>
          </section>
        </div>
      )}
    </div>
  );
}
